import { useCrmLocale, crmText as translateCrmCopy } from '../ui/locale/crmLocale';
import React from 'react';
import { hasReceptionAllBranchesGrant } from '../auth/receptionPermissions';
import CrmApplicationFrame from '../components/crm/CrmApplicationFrame';
import { useQuery } from '@tanstack/react-query';
import { RouterProvider, Routes, Route, useParams, Navigate, useLocation, Outlet } from 'react-router-dom';

import { QUERY_META } from '../queries/core/queryMeta';
import { receptionKeys } from '../queries/reception/receptionKeys';
import { getQueryLoadPhase } from '../queries/core/loadState';
import { ReceptionPageWrapper } from '../components/ReceptionPageWrapper';
import { RealtimeProvider } from '../reception/RealtimeProvider';
import { ThemeProvider } from '../ui/student/ThemeContext';
import { EmployeeThemeProvider } from '../components/employee/EmployeeThemeContext';
import { useAuth } from '../auth/useAuth';
import GlobalPushBootstrap from '../components/GlobalPushBootstrap';
import SimulationBanner from '../components/developer/SimulationBanner';
import OfflineStatusBanner from '../components/OfflineStatusBanner';
import { AccountPreferencesProvider } from '../features/account-settings/AccountPreferencesProvider';
import { PageSkeleton } from '../components/skeletons';
import Forbidden from '../pages/errors/Forbidden';
import { getShellVariantFromPath } from './useShellVariantFromPath';
import { resolveEmployeeLoginPathFromUser } from '../pages/employee/employeeAuthPaths';
import {
  buildLoginTarget,
  CanonicalReplaceRedirect,
  NavigationScopeProvider,
  useNavigationScope,
  resolveAppBasename,
  resolveRoutePolicy,
  toDocumentPath,
  type RouterBasename,
} from '../navigation';
import { createAppRouter } from './appRouter';
import { replaceDocumentLocation } from '../navigation/documentNavigation';
import { isReceptionFinanceShellAllowed } from '../pages/reception/receptionFinanceNavigation';
import ReceptionWorkflowTabs from '../pages/reception/ReceptionWorkflowTabs';

import ManagerPageWrapper from '../components/manager/ManagerPageWrapper';
import {
  fetchReceptionStudentRoutePayload,
  ReceptionRouteLookupError,
} from '../api/reception';

const LocalPrebuilt = React.lazy(() => import('../pages/live/LocalPrebuilt'));
const loadStudentInvoicePage = () => import('../pages/reception/student-invoice/StudentInvoiceIssuePage');
const StudentInvoiceIssuePage = React.lazy(loadStudentInvoicePage);
const ReceptionStudents = React.lazy(() => import('../ReceptionStudents'));
const OldSystemPage = React.lazy(() => import('../pages/reception/old-system/OldSystemPage'));
const ReceptionDashboard = React.lazy(() => import('../ReceptionDashboard'));
const WaitingPage = React.lazy(() => import('../pages/reception/WaitingPage'));
const GroupsPage = React.lazy(() => import('../pages/reception/GroupsPage'));
const StudentRegisterPage = React.lazy(() => import('../pages/reception/StudentRegisterPage'));
const HRDashboardPage = React.lazy(() => import('../pages/HRDashboardPage'));
const ExamCreatePage = React.lazy(() => import('../pages/reception/ExamCreatePage'));
const ExamEditPage = React.lazy(() => import('../pages/reception/ExamEditPage'));
const ExamAuditPage = React.lazy(() => import('../pages/reception/ExamAuditPage'));
const ExamViewPage = React.lazy(() => import('../pages/reception/ExamViewPage'));
const ExamListPage = React.lazy(() => import('../pages/reception/ExamListPage'));
const LanguageAdminMainPage = React.lazy(() => import('../pages/reception/language-admin-main-page'));
const LanguageAdminMaterialsPage = React.lazy(() => import('../pages/reception/language-admin-materials/LanguageAdminMaterialsPage'));
const ImportExamPage = React.lazy(() => import('../pages/reception/ImportExamPage'));
const ImportExamProgressPage = React.lazy(() => import('../pages/reception/ImportExamProgressPage'));
const InactiveStudentsPage = React.lazy(() => import('../pages/reception/InactiveStudentsPage'));
const AttemptDetailPage = React.lazy(() => import('../pages/reception/AttemptDetailPage'));
const AttendanceMatrixIndexPage = React.lazy(() => import('../pages/reception/AttendanceMatrixIndexPage'));
const AttendanceMatrixPage = React.lazy(() => import('../pages/reception/attendance-matrix/AttendanceMatrixPage'));
const GroupAttendancePage = React.lazy(() => import('../pages/reception/GroupAttendancePage'));
const AttendanceSessionPage = React.lazy(() => import('../pages/reception/attendance-session/AttendanceSessionPage'));
const SuperLogsPage = React.lazy(() => import('../pages/reception/super-logs-page/SuperLogsPage'));
const EnhancedEmployeeProfilePage = React.lazy(() => import('../pages/employee/enhanced-profile-page'));
const AccountSettingsPage = React.lazy(() => import('../features/account-settings/AccountSettingsPage'));
const RefundsPage = React.lazy(() => import('../pages/reception/RefundsPage'));
const RequestsPage = React.lazy(() => import('../pages/reception/RequestsPage'));
const CourseInterestsPage = React.lazy(() => import('../pages/reception/CourseInterestsPage'));
const SalesConsultationSlotsPage = React.lazy(() => import('../pages/reception/SalesConsultationSlotsPage'));
const SalesConsultQueuePage = React.lazy(() => import('../pages/reception/SalesConsultQueuePage'));
const QuarterPage = React.lazy(() => import('../pages/reception/QuarterPage'));
const CalendarPage = React.lazy(() => import('../pages/reception/calendar-page'));
const TaskListPage = React.lazy(() => import('../pages/reception/tasks/TaskList'));
const TaskManagementPage = React.lazy(() => import('../pages/reception/tasks/TaskManagement'));
const FinancePage = React.lazy(() => import('../pages/reception/FinancePage'));
const BranchFinancePage = React.lazy(() => import('../pages/reception/BranchFinancePage'));
const SalesInvoicesPage = React.lazy(() => import('../pages/reception/finance/SalesInvoicesPage'));
const CertificatesPage = React.lazy(() => import('../pages/reception/CertificatesPage'));
const EventCertificatesPage = React.lazy(() => import('../pages/reception/EventCertificatesPage'));
const ProFinancePage = React.lazy(() => import('../pages/reception/ProFinancePage'));
const PlacementPaymentsPage = React.lazy(() => import('../pages/reception/placement/PlacementPaymentsPage'));
const DailyReportsPage = React.lazy(() => import('../pages/reception/DailyReportsPage'));
const GroupReportsPage = React.lazy(() => import('../pages/reception/group-reports/GroupReportsPage'));
const BranchSummaryPage = React.lazy(() => import('../pages/reception/BranchSummaryPage'));
const GroupBalancesPage = React.lazy(() => import('../pages/reception/GroupBalancesPage'));
const BookingReportsPage = React.lazy(() => import('../pages/reception/BookingReportsPage'));
const ChatPage = React.lazy(() => import('../pages/reception/ChatPage'));
const InvoiceReceiptPrint = React.lazy(() => import('../pages/reception/InvoiceReceiptPrint'));
const SummerTrainingReceiptPrint = React.lazy(() => import('../pages/reception/summer-training/SummerTrainingReceiptPrint'));
const ReceiptVerificationPage = React.lazy(() => import('../pages/public/ReceiptVerificationPage'));
const PaymentConversionPage = React.lazy(() => import('../pages/public/PaymentConversionPage'));
const Dashboard = React.lazy(() => import('../pages/student/dashboard/Dashboard'));
const Profile = React.lazy(() => import('../pages/student/profile'));
const GroupDetail = React.lazy(() => import('../pages/student/StudentGroupPage'));
const Exams = React.lazy(() => import('../pages/student/Exams'));
const TakeExam = React.lazy(() => import('../pages/student/take-exam/TakeExam'));
const AttemptView = React.lazy(() => import('../pages/student/AttemptView'));
const StudentInvoiceReceiptPrint = React.lazy(() => import('../pages/student/StudentInvoiceReceiptPrint'));
const InstructorDashboard = React.lazy(() => import('../pages/instructor/InstructorDashboard'));
const InstructorGroup = React.lazy(() => import('../pages/instructor/InstructorGroup'));
const InstructorProfile = React.lazy(() => import('../pages/instructor/InstructorProfile'));
const MailCenterPage = React.lazy(() => import('../pages/mail/MailCenterPage'));
const DeveloperCockpitPage = React.lazy(() => import('../pages/developer/DeveloperCockpitPage'));
const HRMailPage = React.lazy(() => import('../pages/hr/HRMailPage'));

const InstructorAttemptReview = React.lazy(() => import('../pages/instructor/InstructorAttemptReview'));
const InstructorLiveHost = React.lazy(() => import('../pages/instructor/InstructorLiveHost'));
const InstructorAttendanceSession = React.lazy(() => import('../pages/instructor/InstructorAttendanceSession'));
const ManagerDashboard = React.lazy(() => import('../pages/manager/ManagerDashboard'));
const EmployeesPage = React.lazy(() => import('../pages/manager/EmployeesPage'));
const ManagerTasksPage = React.lazy(() => import('../pages/manager/TasksPage'));
const BranchTransfersPage = React.lazy(() => import('../pages/manager/BranchTransfersPage'));
const ManagerFinancePage = React.lazy(() => import('../pages/manager/ManagerFinancePage'));
const ManagerProFinancePage = React.lazy(() => import('../pages/manager/ManagerProFinancePage'));
const HREmployeesPage = React.lazy(() => import('../pages/hr/employees-page/EnhancedHREmployeesPage'));
const HRPayrollPage = React.lazy(() => import('../pages/HRPayrollPage'));
const HRAttendancePage = React.lazy(() => import('../pages/hr-attendance/HRAttendancePage'));
const HRMessagesPage = React.lazy(() => import('../pages/hr/EnhancedHRMessagesPage'));
const HRRequestsPage = React.lazy(() => import('../pages/hr/EnhancedHRRequestsPage'));
const HRReportsPage = React.lazy(() => import('../pages/hr/HRReportsPage'));
const HRSchedulesPage = React.lazy(() => import('../pages/hr/HRSchedulesPage'));
const InstructorPayrollWorkspace = React.lazy(() => import('../components/InstructorPayrollWorkspace'));
const HRInstructorPayrollPage = React.lazy(() => import('../pages/hr/HRInstructorPayrollPage'));
const HRGroupRatesPage = React.lazy(() => import('../pages/hr/HRGroupRatesPage'));
const LiveViewer = React.lazy(() => import('../pages/student/LiveViewer'));
const StaffPortalLogin = React.lazy(() => import('../pages/employee/StaffPortalLogin'));
const EmployeeRegister = React.lazy(() => import('../pages/employee/EmployeeRegister'));
const EmployeeForgotPassword = React.lazy(() => import('../pages/employee/EmployeeForgotPassword'));
const EmployeeResetPassword = React.lazy(() => import('../pages/employee/EmployeeResetPassword'));
const EmployeeGoogleFollowup = React.lazy(() => import('../pages/employee/EmployeeGoogleFollowup'));
const EnhancedEmployeeMessagesPage = React.lazy(() => import('../pages/employee/enhanced-messages-page'));
const EnhancedEmployeeRequestsPage = React.lazy(() => import('../pages/employee/enhanced-employee-requests-page'));
const EmployeeAttendancePage = React.lazy(() => import('../pages/employee/employee-attendance-page'));
const SalesApp = React.lazy(() => import('../pages/sales/SalesApp'));
const ImportLeadsPage = React.lazy(() => import('../pages/sales/ImportLeadsPage'));
const MarketingAnalyticsPage = React.lazy(() => import('../pages/marketing/MarketingAnalyticsPage'));
const MarketingDashboardPage = React.lazy(() => import('../pages/marketing/MarketingDashboardPage'));
const CourseCatalogPage = React.lazy(() => import('../pages/marketing/CourseCatalogPage'));
const MarketingShell = React.lazy(() => import('../pages/marketing/MarketingShell'));
const ModeratorPage = React.lazy(() => import('../pages/marketing/ModeratorPage'));
const ModeratorResultsPage = React.lazy(() => import('../pages/marketing/ModeratorResultsPage'));
const MarketingWorkboardsPage = React.lazy(() => import('../pages/marketing/workboards/MarketingWorkboardsPage'));
const JobApplicationsPage = React.lazy(() => import('../pages/marketing/JobApplicationsPage'));
const ContactInquiriesPage = React.lazy(() => import('../pages/marketing/ContactInquiriesPage'));
const InternshipRegistrationsPage = React.lazy(() => import('../pages/marketing/InternshipRegistrationsPage'));
const MarketingSalesInvoicesPage = React.lazy(() => import('../pages/marketing/MarketingSalesInvoicesPage'));
const PublicJobApplicationForm = React.lazy(() => import('../pages/job-applications/PublicJobApplicationForm'));
const PlacementRegister = React.lazy(() => import('../pages/public/placement/PlacementRegister'));
const PlacementExam = React.lazy(() => import('../pages/public/placement/PlacementExam'));
const PlacementStatus = React.lazy(() => import('../pages/public/placement/PlacementStatus'));
const PlacementContinue = React.lazy(() => import('../pages/public/placement/PlacementContinue'));
const NotFoundPage = React.lazy(() => import('../pages/errors/NotFound'));
const UnauthorizedPage = React.lazy(() => import('../pages/errors/Unauthorized'));
const BadRequestPage = React.lazy(() => import('../pages/errors/BadRequest'));
const InternalServerErrorPage = React.lazy(() => import('../pages/errors/InternalServerError'));
const ServiceUnavailablePage = React.lazy(() => import('../pages/errors/ServiceUnavailable'));
const NetworkErrorPage = React.lazy(() => import('../pages/errors/NetworkError'));
const LinkExpiredPage = React.lazy(() => import('../pages/errors/LinkExpired'));
const AccountantRefunds = React.lazy(() => import('../pages/accountant/AccountantRefunds'));
const AccountantShell = React.lazy(() => import('../pages/accountant/AccountantShell'));
const AccountantDashboardNew = React.lazy(() => import('../pages/accountant/AccountantDashboardNew'));
const AccountantIncomePage = React.lazy(() => import('../pages/accountant/AccountantIncomePage'));
const AccountantSalesCollectionsPage = React.lazy(() => import('../pages/accountant/AccountantSalesCollectionsPage'));
const AccountantExpensePage = React.lazy(() => import('../pages/accountant/AccountantExpensePage'));
const AccountantReportsPage = React.lazy(() => import('../pages/accountant/AccountantReportsPage'));
const AccountantCashFlowPage = React.lazy(() => import('../pages/accountant/AccountantCashFlowPage'));
const AccountantInstallmentsPage = React.lazy(() => import('../pages/accountant/AccountantInstallmentsPage'));
const AccountantGroupRatesPage = React.lazy(() => import('../pages/accountant/AccountantGroupRatesPage'));
const AccountantCustomerEstimatesPage = React.lazy(() => import('../pages/accountant/AccountantCustomerEstimatesPage'));
const PrivacyPolicy = React.lazy(() => import('../pages/public/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('../pages/public/TermsOfService'));
const PublicLayout = React.lazy(() => import('../pages/public/PublicLayout'));
const About = React.lazy(() => import('../pages/public/About'));
const Contact = React.lazy(() => import('../pages/public/Contact'));
const Courses = React.lazy(() => import('../pages/public/Courses'));
const CourseDetails = React.lazy(() => import('../pages/public/CourseDetails'));
const Programs = React.lazy(() => import('../pages/public/Programs'));
const PlacementTest = React.lazy(() => import('../pages/public/PlacementTest'));
const HealthDashboard = React.lazy(() => import('../pages/diagnostics/HealthDashboard'));
const DiagnosticsEndpointJson = React.lazy(() => import('../pages/diagnostics/DiagnosticsEndpointJson'));
const SummerTrainingApp = React.lazy(() => import('../features/summer-training/SummerTrainingApp'));

const Home = React.lazy(() => import('../pages/public/Home'));
const EventCertificatePage = React.lazy(() => import('../pages/public/EventCertificatePage'));
const Login = React.lazy(() => import('../pages/auth/Login'));
const Register = React.lazy(() => import('../pages/auth/Register'));
const ForgotPassword = React.lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../pages/auth/ResetPassword'));
const SetPassword = React.lazy(() => import('../pages/auth/SetPassword'));
const GoogleFollowup = React.lazy(() => import('../pages/auth/GoogleFollowup'));

function RouteSkeletonFallback() {
  const location = useLocation();
  return <PageSkeleton variant={getShellVariantFromPath(location.pathname)} />;
}

function RootLocationRedirect({ to, variant }: { to: string; variant: React.ComponentProps<typeof PageSkeleton>['variant'] }) {
  React.useEffect(() => {
    replaceDocumentLocation(to);
  }, [to]);
  return <PageSkeleton variant={variant} />;
}

export function EmployeeAuthShell() {
  return (
    <React.Suspense fallback={<PageSkeleton variant="employee" />}>
      <Routes>
        <Route path="/employee/login" element={<StaffPortalLogin />} />
        <Route path="/employee/register" element={<EmployeeRegister />} />
        <Route path="/employee/forgot-password" element={<EmployeeForgotPassword />} />
        <Route path="/employee/reset-password" element={<EmployeeResetPassword />} />
        <Route path="/employee/auth/google-followup" element={<EmployeeGoogleFollowup />} />
        <Route path="/employee/*" element={<StaffPortalLogin />} />
        <Route path="*" element={<StaffPortalLogin />} />
      </Routes>
    </React.Suspense>
  );
}

function AppRouterLifetime({ basename }: { basename: RouterBasename }) {
  const [router, setRouter] = React.useState<ReturnType<typeof createAppRouter> | null>(null);
  // The router owns same-basename navigation. Reading the changing address bar
  // here would dispose that router on an unrelated parent render, losing its
  // in-flight transition, blockers and Back/Forward history subscription.
  const [documentPathname] = React.useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : basename);

  React.useEffect(() => {
    const nextRouter = createAppRouter(
      <NavigationScopeProvider
        basename={basename}
        pendingFallback={<PageSkeleton variant={getShellVariantFromPath(documentPathname)} />}
      >
        <RoutedAppShell basename={basename} />
      </NavigationScopeProvider>,
      basename,
    );
    setRouter(nextRouter);
    return () => nextRouter.dispose();
  }, [basename, documentPathname]);

  if (!router) {
    return <PageSkeleton variant={getShellVariantFromPath(documentPathname)} />;
  }
  return <RouterProvider router={router} />;
}

export function ReceptionStudentsRoute() {
  return <ReceptionStudents />;
}

export function ReceptionWaitingAliasRedirect() {
  const location = useLocation();
  const source = new URLSearchParams(location.search);
  const target = new URLSearchParams({ category: 'waiting' });
  for (const [key, value] of source.entries()) {
    if (key !== 'category') target.append(key, value);
  }
  return <CanonicalReplaceRedirect to={{
    pathname: '/reception/students',
    search: `?${target.toString()}`,
    hash: location.hash,
  }} />;
}

export function ReceptionMaterialsAliasRedirect() {
  const location = useLocation();
  return <CanonicalReplaceRedirect to={{
    pathname: '/reception/language/materials',
    search: location.search,
    hash: location.hash,
  }} />;
}

export function ReceptionRootStudentsRedirect() {
  const location = useLocation();
  return <CanonicalReplaceRedirect to={{
    pathname: '/reception/students',
    search: location.search,
    hash: location.hash,
  }} />;
}

function ProtectedLoginRedirect({
  basename,
  loginPath,
  variant,
}: {
  basename: RouterBasename;
  loginPath: string;
  variant: React.ComponentProps<typeof PageSkeleton>['variant'];
}) {
  const location = useLocation();
  const requestedDocumentLocation = `${toDocumentPath(basename, location.pathname)}${location.search}${location.hash}`;
  const loginTarget = buildLoginTarget(loginPath, requestedDocumentLocation);
  const loginPolicy = resolveRoutePolicy({ basename, pathname: loginPath });

  if (!loginPolicy || loginPolicy.shell !== 'auth') {
    return <RootLocationRedirect to={loginTarget} variant={variant} />;
  }

  return <Navigate to={loginTarget} replace />;
}

export function MainAppShell() {
  const documentPathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const basename = resolveAppBasename(documentPathname);
  return <AppRouterLifetime key={basename} basename={basename} />;
}

function ReceptionFeatureRoute({ allowed, children }: { allowed: boolean; children: React.ReactElement }) {
  const { user, bootstrapResolved } = useAuth();
  const { basename } = useNavigationScope();
  const loginPath = resolveEmployeeLoginPathFromUser();
  if (!bootstrapResolved) return <PageSkeleton variant="reception" />;
  if (!user) return <ProtectedLoginRedirect basename={basename} loginPath={loginPath} variant="reception" />;
  if (!allowed) return <Forbidden />;
  return children;
}

export function RoutedAppShell({ basename }: { basename: RouterBasename }) {
  const documentPathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isManagerBase = basename === '/manager';
  const isHRBase = basename === '/hr';
  const isAccountantBase = basename === '/accountant';
  const employeeLoginPath = resolveEmployeeLoginPathFromUser();
  const isNestedEmployeeAuthPath = /^\/(manager|hr|accountant)\/employee\/(login|register|forgot-password|reset-password)/.test(documentPathname);

  const { user: authUser, bootstrapResolved } = useAuth();
  const authPending = !bootstrapResolved;
  const role = (authUser?.role ? String(authUser.role) : '').toLowerCase();
  const hasReceptionRole = role.includes('reception') || (role.includes('manager') && role !== 'sales_manager');
  const hasManagerRole = role.includes('manager') && role !== 'sales_manager';
  const isGeneralManager = role === 'general_manager';
  const hasAccountantRole = role === 'accountant' || isGeneralManager;
  const hasMarketingRole = role === 'marketing';
  const hasMarketingAccess = hasMarketingRole || role === "moderator" || isGeneralManager;
  const hasSalesRole = role === 'team_leader' || role === 'sales' || role === 'agent' || role === 'sales_manager' || role === 'tele_sales' || role === 'ghosted_sales' || isGeneralManager;
  const hasHRRole = role.includes('hr') || isGeneralManager;
  const hasInstructorRole = role.includes('instructor');
  const hasDeveloperRole = role === 'developer' || Boolean(authUser?.is_developer);
  const realtimeEnabled = !!authUser && (hasReceptionRole || hasAccountantRole);
  const canAccessReception = realtimeEnabled && hasReceptionRole;
  const isSuperReception = role === 'super_reception' || isGeneralManager || hasReceptionAllBranchesGrant(authUser);
  const canAccessSuperReceptionTools = canAccessReception && isSuperReception;
  const canAccessLanguageTools = canAccessReception && (role === 'reception' || isSuperReception);
  const canAccessReceptionFinanceTools = canAccessReception && isReceptionFinanceShellAllowed({
    role,
    username: authUser?.username,
  });
  React.useEffect(() => {
    if (typeof window !== 'undefined' && isNestedEmployeeAuthPath) {
      window.location.replace(employeeLoginPath);
    }
  }, [employeeLoginPath, isNestedEmployeeAuthPath]);

  if (typeof window !== 'undefined' && isNestedEmployeeAuthPath) {
    return <PageSkeleton variant={getShellVariantFromPath(documentPathname)} />;
  }

  function ReceptionEntry() {
    if (authPending) return <PageSkeleton variant="reception" />;
    if (!authUser) return (
      <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="reception" />
    );
    if (!hasReceptionRole) return <Navigate to="/student/dashboard" replace />;
    return <ReceptionDashboard />;
  }

  function NotFoundOrLoading() {
    const loc = useLocation();
    if (authPending) return <PageSkeleton variant={getShellVariantFromPath(loc.pathname)} />;
    return <NotFoundPage />;
  }

  function StudentOnlyRoute({ children }: { children: React.ReactElement }) {
    if (authPending) return <PageSkeleton variant="student" />;
    if (!authUser) return (
      <ProtectedLoginRedirect basename={basename} loginPath="/login" variant="student" />
    );
    const studentRole = String(authUser.role || '').toLowerCase();
    if (studentRole === 'student') return children;
    if (hasReceptionRole) return <Navigate to="/reception" replace />;
    return <Navigate to="/employee/profile" replace />;
  }

  return (
    <AccountPreferencesProvider><RealtimeProvider enabled={realtimeEnabled}>
      <GlobalPushBootstrap />
      <SimulationBanner />
      <OfflineStatusBanner authenticated={Boolean(authUser)} />
      <CrmApplicationFrame>
      <React.Suspense fallback={<RouteSkeletonFallback />}>
        <Routes>
            <Route path="/settings" element={<ReceptionFeatureRoute allowed={Boolean(authUser && role && role !== 'student')}><AccountSettingsPage /></ReceptionFeatureRoute>} />
            {/* Public route - no auth required */}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route path="/job-applications" element={<PublicJobApplicationForm />} />
            )}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <>
                <Route path="/summer-training/*" element={<SummerTrainingApp />} />
                <Route path="/summer-internship-2026" element={<Navigate to="/summer-training/student/register" replace />} />
              </>
            )}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/"
                element={
                  authPending ? (
                    <PageSkeleton variant="public" />
                  ) : authUser ? (
                    hasDeveloperRole ? (
                      <Navigate to="/developer/cockpit" replace />
                    ) : canAccessReception ? (
                      <ReceptionRootStudentsRedirect />
                    ) : (
                      <ThemeProvider><Dashboard /></ThemeProvider>
                    )
                  ) : (
                    <PublicLayout />
                  )
                }
              >
                <Route index element={<Home />} />
              </Route>
            )}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route path="/convert/:token" element={<PaymentConversionPage />} />
            )}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
               <Route element={<PublicLayout />}>
                   <Route path="/about" element={<About />} />
                   <Route path="/contact" element={<Contact />} />
                   <Route path="/course" element={<Courses />} />
                   <Route path="/course/:slug" element={<CourseDetails />} />
                   <Route path="/programs" element={<Programs />} />
                   <Route path="/event-certificate" element={<EventCertificatePage />} />
                   <Route path="/placement-test" element={<PlacementTest />} />
                   <Route path="/privacy" element={<PrivacyPolicy />} />
                   <Route path="/termsofservice" element={<TermsOfService />} />
               </Route>
            )}

            {/* Public Placement Test routes - no auth required */}
            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/auth/google-followup" element={<GoogleFollowup />} />

                <Route path="/placement" element={<PlacementRegister />} />
                <Route path="/placement/continue/:token" element={<PlacementContinue />} />
                <Route path="/placement/exam" element={<PlacementExam />} />
                <Route path="/placement/payment" element={<Navigate to="/placement/status" replace />} />
                <Route path="/placement/status" element={<PlacementStatus />} />
                <Route path="/placement/results" element={<Navigate to="/placement/status" replace />} />
                <Route path="/placement/link-expired" element={<LinkExpiredPage />} />

                {/* Diagnostics */}
                <Route path="/health-dashboard" element={<HealthDashboard />} />
                <Route path="/diagnostics/dashboard" element={<HealthDashboard />} />
                <Route path="/diagnostics/ws-health" element={<DiagnosticsEndpointJson />} />
                <Route path="/diagnostics/bundle/health" element={<DiagnosticsEndpointJson />} />
              </>
            )}

            <Route path="/reception" element={<ReceptionPageWrapper><ReceptionEntry /></ReceptionPageWrapper>} />
            {isManagerBase && hasManagerRole && (
              <>
              <Route element={<ManagerPageWrapper />}>
                <Route path="/" element={<ManagerDashboard />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/tasks" element={<ManagerTasksPage />} />
                <Route path="/mail" element={<MailCenterPage embedded />} />
                <Route path="/instructor-payroll" element={<InstructorPayrollWorkspace />} />
                <Route path="/branch-transfers" element={<BranchTransfersPage />} />
                <Route path="/groups" element={<GroupsPage />} />
                <Route path="/attendance/group/:groupId" element={<GroupAttendancePage />} />
                <Route path="/attendance/session/:sessionId" element={<AttendanceSessionPage />} />
                 <Route path="/quarter" element={<QuarterPage />} />
                 <Route path="/students" element={<ReceptionStudentsRoute />} />
                 <Route path="/students/:id/invoice" element={<InvoiceRoute />} />
                 <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/super/logs" element={<SuperLogsPage />} />
                <Route path="/finance" element={<ManagerFinancePage />} />
                <Route path="/pro-finance" element={<ManagerProFinancePage />} />
                 <Route path="/import-leads" element={<ImportLeadsPage />} />
               </Route>
               <Route path="/invoices/:invoiceId" element={<InvoiceReceiptPrint />} />
               </>
            )}
            {isManagerBase && authPending && (
              <Route path="*" element={<PageSkeleton variant="manager" />} />
            )}
            {isManagerBase && !authPending && !authUser && <Route path="*" element={<ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="manager" />} />}
            {isManagerBase && !authPending && authUser && !hasManagerRole && <Route path="*" element={<Forbidden />} />}

            {isHRBase && hasHRRole && (
              <>
                <Route path="/" element={<HRDashboardPage />} />
                <Route path="/dashboard" element={<HRDashboardPage />} />
                <Route path="/employees" element={<HREmployeesPage />} />
                <Route path="/schedules" element={<HRSchedulesPage />} />
                <Route path="/messages" element={<HRMessagesPage />} />
                <Route path="/mail" element={<HRMailPage />} />
                <Route path="/requests" element={<HRRequestsPage />} />
                <Route path="/payroll" element={<HRPayrollPage />} />
                <Route path="/instructor-payroll" element={<HRInstructorPayrollPage />} />
                <Route path="/group-rates" element={<HRGroupRatesPage />} />
                <Route path="/attendance" element={<HRAttendancePage />} />
                <Route path="/reports" element={<HRReportsPage />} />
              </>
            )}
            {isHRBase && authPending && (
              <Route path="*" element={<PageSkeleton variant="hr" />} />
            )}
            {isHRBase && !authPending && !authUser && <Route path="*" element={<ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="hr" />} />}
            {isHRBase && !authPending && authUser && !hasHRRole && <Route path="*" element={<Forbidden />} />}

            {isAccountantBase && hasAccountantRole && (
              <>
                <Route path="/" element={<AccountantShell />}>
                  <Route path="instructor-payroll" element={<InstructorPayrollWorkspace />} />
                  <Route index element={<AccountantDashboardNew />} />
                  <Route path="dashboard" element={<AccountantDashboardNew />} />
                  <Route path="income" element={<AccountantIncomePage />} />
                  <Route path="sales-collections" element={<AccountantSalesCollectionsPage />} />
                  <Route path="expenses" element={<AccountantExpensePage />} />
                  <Route path="refunds" element={<AccountantRefunds />} />
                  <Route path="customer-estimates" element={<AccountantCustomerEstimatesPage />} />
                  <Route path="reports" element={<AccountantReportsPage />} />
                  <Route path="cash-flow" element={<AccountantCashFlowPage />} />
                  <Route path="installments" element={<AccountantInstallmentsPage />} />
                  <Route path="group-rates" element={<AccountantGroupRatesPage />} />
                </Route>
                <Route path="*" element={<AccountantShell />}>
                  <Route index element={<AccountantDashboardNew />} />
                </Route>
              </>
            )}
            {isAccountantBase && authPending && (
              <Route path="*" element={<PageSkeleton variant="accountant" />} />
            )}
            {isAccountantBase && !authPending && !authUser && <Route path="*" element={<ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="accountant" />} />}
            {isAccountantBase && !authPending && authUser && !hasAccountantRole && <Route path="*" element={<Forbidden />} />}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route path="/sales/*" element={
                authPending ? (
                  <PageSkeleton variant="sales" />
                ) : !authUser ? (
                  <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="sales" />
                ) : hasSalesRole ? (
                  <SalesApp />
                ) : (
                  <Forbidden />
                )
              } />
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route path="/marketing/work/*" element={
                authPending ? <PageSkeleton variant="marketing" /> : !authUser ? (
                  <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="marketing" />
                ) : role === 'moderator' ? <Forbidden /> : <MarketingWorkboardsPage key={`${authUser.id}-${authUser.simulation?.target_employee_id??''}`} principalKey={`${authUser.id}-${authUser.simulation?.target_employee_id??''}`} />
              } />
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/marketing"
                element={
                  authPending ? (
                    <PageSkeleton variant="marketing" />
                  ) : !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="marketing" />
                  ) : hasMarketingAccess ? (
                    <MarketingShell />
                  ) : (
                    <Forbidden />
                  )
                }
              >
                <Route index element={<Navigate to={role === "moderator" ? "/marketing/moderator" : "/marketing/dashboard"} replace />} />
                <Route path="moderator" element={<ModeratorPage />} />
                <Route path="moderator/follow-ups" element={role==='moderator'?<ModeratorPage surface="work_queue"/>:<Forbidden/>} />
                <Route path="moderator/results" element={<ModeratorResultsPage/>} />
                <Route path="dashboard" element={role === "moderator" ? <Forbidden /> : <MarketingAnalyticsPage />} />
                <Route path="courses" element={role === "moderator" ? <Forbidden /> : <MarketingDashboardPage />} />
                <Route path="catalog" element={role === "moderator" ? <Forbidden /> : <CourseCatalogPage />} />
                <Route path="job-applications" element={role === "moderator" ? <Forbidden /> : <JobApplicationsPage />} />
                <Route path="contact-inquiries" element={role === "moderator" ? <Forbidden /> : <ContactInquiriesPage />} />
                <Route path="internship-registrations" element={role === 'moderator' ? <Forbidden /> : <InternshipRegistrationsPage />} />
                <Route path="mail" element={role === "moderator" ? <Forbidden /> : <MailCenterPage embedded />} />
                <Route path="sales-invoices" element={role === "moderator" ? <Forbidden /> : <MarketingSalesInvoicesPage />} />
                <Route path="*" element={<Navigate to="/marketing/courses" replace />} />
              </Route>
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/mail"
                element={
                  authPending ? (
                    <PageSkeleton variant="employee" />
                  ) : authUser ? (
                    <MailCenterPage />
                  ) : (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />
                  )
                }
              />
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/developer/cockpit"
                element={
                  authPending ? (
                    <PageSkeleton variant="generic" />
                  ) : !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="generic" />
                  ) : hasDeveloperRole ? (
                    <DeveloperCockpitPage defaultTab="cockpit" />
                  ) : (
                    <Forbidden />
                  )
                }
              />
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/developer/errors"
                element={
                  authPending ? (
                    <PageSkeleton variant="generic" />
                  ) : !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="generic" />
                  ) : hasDeveloperRole ? (
                    <DeveloperCockpitPage defaultTab="errors" />
                  ) : (
                    <Forbidden />
                  )
                }
              />
            )}

            {!isManagerBase && !isHRBase && !isAccountantBase && (
              <Route
                path="/developer/sandbox"
                element={
                  authPending ? (
                    <PageSkeleton variant="generic" />
                  ) : !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="generic" />
                  ) : hasDeveloperRole ? (
                    <DeveloperCockpitPage defaultTab="sandbox" />
                  ) : (
                    <Forbidden />
                  )
                }
              />
            )}

            <Route path="/receipt/verify/:token" element={<ReceiptVerificationPage />} />
            {canAccessReception && (
              <>
                <Route path="/reception/summer-training" element={<Navigate to="/summer-training/operations" replace />} />
                <Route element={<ReceptionPageWrapper><Outlet /></ReceptionPageWrapper>}>
                  <Route element={<ReceptionWorkflowTabs group="waiting" />}>
                    <Route path="/reception/students" element={<ReceptionStudentsRoute />} />
                    <Route path="/reception/waiting-insights" element={<WaitingPage />} />
                    <Route path="/reception/waiting" element={<ReceptionWaitingAliasRedirect />} />
                  </Route>
                  <Route path="/reception/groups" element={<GroupsPage />} />
                  <Route path="/reception/old-system" element={<OldSystemPage />} />
                  <Route path="/reception/old-system/:sourceId/:legacyId" element={<OldSystemPage />} />
                  <Route path="/reception/quarter" element={<QuarterPage />} />
                  <Route path="/reception/calendar" element={<CalendarPage />} />
                  <Route path="/reception/finance" element={<ReceptionFeatureRoute allowed={canAccessReceptionFinanceTools}><FinancePage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/branch-finance" element={<BranchFinancePage />} />
                  <Route path="/reception/sales-invoices" element={<SalesInvoicesPage />} />
                  <Route path="/reception/certificates" element={<CertificatesPage />} />
                  <Route path="/reception/event-certificates" element={<EventCertificatesPage />} />
                  <Route path="/reception/mail" element={<MailCenterPage embedded />} />
                  <Route path="/reception/pro-finance" element={<ReceptionFeatureRoute allowed={canAccessReceptionFinanceTools}><ProFinancePage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/placement-payments" element={<PlacementPaymentsPage />} />
                  <Route path="/reception/refunds" element={<RefundsPage />} />
                  <Route element={<ReceptionWorkflowTabs group="requests" />}>
                    <Route path="/reception/requests" element={<RequestsPage />} />
                    <Route path="/reception/course-interests" element={<CourseInterestsPage />} />
                  </Route>
                  <Route element={<ReceptionWorkflowTabs group="consultations" />}>
                    <Route path="/reception/sales-consult-slots" element={<SalesConsultationSlotsPage />} />
                    <Route path="/reception/sales-consult-queue" element={<SalesConsultQueuePage />} />
                  </Route>
                  <Route path="/reception/tasks" element={<TaskListPage />} />
                  <Route path="/reception/tasks-manage" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><TaskManagementPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/daily-reports" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><DailyReportsPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/reports" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><GroupReportsPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/reports/summary" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><BranchSummaryPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/reports/group-balances" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><GroupBalancesPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/reports/bookings" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><BookingReportsPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/chat" element={<ChatPage />} />
                  <Route path="/reception/super/logs" element={<ReceptionFeatureRoute allowed={canAccessSuperReceptionTools}><SuperLogsPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/students/register" element={<StudentRegisterPage />} />
                  <Route path="/reception/students/:id/invoice" element={<InvoiceRoute />} />
                  <Route path="/reception/exams/new" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ExamCreatePage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/exams" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ExamListPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/exams/:id/edit" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ExamEditPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/exams/:id/audit" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ExamAuditPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/exams/:id/view" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ExamViewPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/attempts/:id" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><AttemptDetailPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/attendance/matrix" element={<AttendanceMatrixIndexPage />} />
                  <Route path="/reception/attendance/matrix/:groupId" element={<AttendanceMatrixPage />} />
                  <Route path="/reception/attendance/group/:groupId" element={<GroupAttendancePage />} />
                  <Route path="/reception/attendance/session/:sessionId" element={<AttendanceSessionPage />} />
                  <Route path="/reception/language" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><LanguageAdminMainPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/language/exams/import" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ImportExamPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/language/exams/import/progress/:jobId" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ImportExamProgressPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/language/materials" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><LanguageAdminMaterialsPage /></ReceptionFeatureRoute>} />
                  <Route path="/reception/materials" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><ReceptionMaterialsAliasRedirect /></ReceptionFeatureRoute>} />
                  <Route path="/reception/language/students/inactive" element={<ReceptionFeatureRoute allowed={canAccessLanguageTools}><InactiveStudentsPage /></ReceptionFeatureRoute>} />
                </Route>
                {/* Receipt pages outside shell for printing */}
                <Route path="/reception/invoices/:invoiceId" element={<InvoiceReceiptPrint />} />
                <Route path="/reception/summer-training/receipts/:receiptId" element={<SummerTrainingReceiptPrint />} />
              </>
            )}
            {!canAccessReception && !authPending && (
              <Route
                path="/reception/*"
                element={
                  !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="reception" />
                  ) : (
                    <Navigate to="/student/dashboard" replace />
                  )
                }
              />
            )}

            <Route path="/student/dashboard" element={<StudentOnlyRoute><ThemeProvider><Dashboard /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/student/profile" element={<StudentOnlyRoute><ThemeProvider><Profile /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/languages/student/profile" element={<StudentOnlyRoute><ThemeProvider><Profile /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/student/groups/:id" element={<StudentOnlyRoute><ThemeProvider><GroupDetail /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/languages/student/exams" element={<StudentOnlyRoute><ThemeProvider><Exams /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/languages/student/exam/:examId/start" element={<StudentOnlyRoute><ThemeProvider><TakeExam /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/languages/student/exam/attempt/:attemptId" element={<StudentOnlyRoute><ThemeProvider><AttemptView /></ThemeProvider></StudentOnlyRoute>} />
            {hasInstructorRole && (
              <>
                <Route path="/instructor" element={<InstructorDashboard />} />
                <Route path="/instructor/profile" element={<InstructorProfile />} />
                <Route path="/instructor/groups/:id" element={<InstructorGroup />} />
                <Route path="/instructor/groups/:id/live" element={<InstructorLiveHost />} />
                <Route path="/instructor/attempts/:attemptId" element={<InstructorAttemptReview />} />
                <Route path="/instructor/payroll" element={<InstructorPayrollWorkspace />} />
                <Route path="/instructor/attendance/session/:sessionId" element={<InstructorAttendanceSession />} />
              </>
            )}
            {(authPending || !authUser || !hasInstructorRole) && (
              <Route
                path="/instructor/*"
                element={
                  authPending ? (
                    <PageSkeleton variant="employee" />
                  ) : !authUser ? (
                    <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />
                  ) : (
                    <Forbidden />
                  )
                }
              />
            )}
            <Route path="/student/exams/:examId/start" element={<StudentOnlyRoute><ThemeProvider><TakeExam /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/student/live/viewer" element={<StudentOnlyRoute><ThemeProvider><LiveViewer /></ThemeProvider></StudentOnlyRoute>} />
            <Route path="/student/invoice/:invoiceId" element={<StudentOnlyRoute><StudentInvoiceReceiptPrint /></StudentOnlyRoute>} />
            <Route path="/employee" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <Navigate to="/employee/profile" replace /> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/instructor-payroll" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <InstructorPayrollWorkspace /> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/profile" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <EmployeeThemeProvider><EnhancedEmployeeProfilePage /></EmployeeThemeProvider> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/messages" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <EmployeeThemeProvider><EnhancedEmployeeMessagesPage /></EmployeeThemeProvider> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/mail" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <EmployeeThemeProvider><MailCenterPage /></EmployeeThemeProvider> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/requests" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <EmployeeThemeProvider><EnhancedEmployeeRequestsPage /></EmployeeThemeProvider> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/attendance" element={authPending ? <PageSkeleton variant="employee" /> : authUser ? <EmployeeAttendancePage /> : <ProtectedLoginRedirect basename={basename} loginPath={employeeLoginPath} variant="employee" />} />
            <Route path="/employee/login" element={<StaffPortalLogin />} />
            <Route path="/employee/register" element={<EmployeeRegister />} />
            <Route path="/employee/forgot-password" element={<EmployeeForgotPassword />} />
            <Route path="/employee/reset-password" element={<EmployeeResetPassword />} />
            <Route path="/app-shell" element={<Navigate to="/login" replace />} />
            <Route path="/app-shell.html" element={<Navigate to="/login" replace />} />
            <Route path="/live/prebuilt" element={<LocalPrebuilt />} />
            <Route path="/error/404" element={<NotFoundPage />} />
            <Route path="/error/403" element={<Forbidden />} />
            <Route path="/error/401" element={<UnauthorizedPage />} />
            <Route path="/error/400" element={<BadRequestPage />} />
            <Route path="/error/500" element={<InternalServerErrorPage />} />
            <Route path="/error/503" element={<ServiceUnavailablePage />} />
            <Route path="/error/network" element={<NetworkErrorPage />} />
            <Route path="*" element={<NotFoundOrLoading />} />
        </Routes>
      </React.Suspense>
      </CrmApplicationFrame>
    </RealtimeProvider></AccountPreferencesProvider>
  );
}

function renderRouteError(errorMessage: string): React.ReactElement {
  return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-exclamation-triangle text-2xl text-red-500" />
      </div>
      <p className="text-red-600 mb-4">{errorMessage}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >{translateCrmCopy("Retry")}</button>
    </div>
  );
}

function mapInvoiceRouteError(error: unknown): string {
  if (!(error instanceof ReceptionRouteLookupError)) return 'Network error. Please check your connection.';
  if (error.code === 'unauthorized') return 'Session expired. Please log in again.';
  if (error.code === 'forbidden') return 'Access denied. You may not have permission to view this student.';
  if (error.code === 'not_found') return 'Student not found.';
  if (error.code === 'timeout') return 'Request timed out. Please check your connection and try again.';
  if (error.code === 'unknown') return 'Failed to load student data. Please try again.';
  return 'Network error. Please check your connection.';
}

function InvoiceRoute() {
  const { t: crmText } = useCrmLocale();

  const { id } = useParams();
  const sid = Number(id);
  const validSid = Number.isSafeInteger(sid) && sid > 0;
  React.useEffect(() => {
    // Download the editor while its authorized identity lookup is in flight.
    if (validSid) void loadStudentInvoicePage().catch(() => undefined);
  }, [validSid]);

  const query = useQuery({
    queryKey: [...receptionKeys.students.detail(sid), 'invoice-context'],
    queryFn: ({ signal }) => fetchReceptionStudentRoutePayload(sid, signal),
    enabled: validSid,
    retry: 0,
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
    placeholderData: undefined,
  });

  const payloadStudentId = Number(query.data?.student?.id ?? query.data?.id);
  const scopedData = validSid && payloadStudentId === sid ? query.data : undefined;

  const loadPhase = getQueryLoadPhase({
    isFetching: query.isFetching,
    isError: Boolean(query.error),
    hasData: Boolean(scopedData),
  });

  if (!validSid) return <div className="p-4 text-red-600">{crmText("Invalid student id")}</div>;
  if (loadPhase === 'initial') return <PageSkeleton variant="reception" />;
  if (query.error && !scopedData) return renderRouteError(mapInvoiceRouteError(query.error));
  if (!scopedData) return renderRouteError('Unable to load student data.');

  const courses: string[] = Array.isArray(scopedData.courses)
    ? scopedData.courses
    : (Array.isArray(scopedData.student?.courses) ? scopedData.student.courses : []);

  const studentCode = scopedData.student_code || scopedData.student?.student_code || '';
  const studentName = scopedData.full_name || scopedData.student?.full_name || scopedData.student_name || scopedData.student?.student_name || scopedData.name || scopedData.student?.name || '';
  const intendedCourse = scopedData.intended_course || scopedData.student?.intended_course;
  return (
    <StudentInvoiceIssuePage
      studentId={sid}
      studentCode={studentCode}
      studentName={studentName}
      intendedCourse={intendedCourse}
      courses={courses}
    />
  );
}
